import {
  faBlockQuestion,
  faCircleInfo,
  faCirclePlay,
  faClosedCaptioning,
  faGlobe,
  faRectanglesMixed,
  faShapes,
} from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Card, CardMedia, styled, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'

import CywarenessDefaultImage from '@/assets/images/cywareness_default.png'
import FullModal from '@/common/components/FullModal'
import Tags from '@/common/components/Tags/Tags'
import VideoPlayerV2 from '@/common/components/VideoPlayerV2/VideoPlayerV2'
import { resizeImage } from '@/common/utils/cloudinary-utils'
import { theme } from '@/theme/theme'
import { VideoResult } from '@/types/videos'

type VideoPreviewParams = { video: VideoResult }

const VideoPreview: FC<VideoPreviewParams> = ({ video }) => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)

  const closeModal = () => {
    setIsModalOpen(false)
  }

  const openModal = () => {
    setIsModalOpen(true)
  }

  return (
    <>
      <Box gap={4} display="flex">
        <StyledThumbnail onClick={openModal}>
          <CardMedia
            component="img"
            height={264}
            alt={`Image of ${video.thumbnail}`}
            image={resizeImage(video.thumbnail || CywarenessDefaultImage, 300, 200)}
            title={video.title}
          />
          <StyledPlayWrapper>
            <FontAwesomeIcon icon={faCirclePlay} fontSize={80} />
          </StyledPlayWrapper>
          <StyledTimeWrapper>{dayjs().startOf('day').add(video.duration, 'second').format('mm:ss')}</StyledTimeWrapper>
        </StyledThumbnail>
        <Box display="flex" gap={3} flexDirection="column" justifyContent="center">
          <Box display="flex" gap={1} alignItems="center">
            <FontAwesomeIcon icon={faRectanglesMixed} fontSize={24} />
            <Typography>{t('campaignDashboard.yourVideo.info.title')}</Typography>
            <Typography fontWeight={theme.typography.fontWeightBold}>{video.title}</Typography>
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <FontAwesomeIcon icon={faGlobe} fontSize={24} />
            <Typography>{t('campaignDashboard.yourVideo.info.dubbing')}</Typography>
            <Tags
              fontWeight={theme.typography.fontWeightBold}
              useChips={false}
              tags={Object.keys(video.video_tracks).map((tag) => ({
                label: tag.toUpperCase(),
              }))}
              limit={7}
            />
          </Box>
          <Box display="flex" gap={1} alignItems="center">
            <FontAwesomeIcon icon={faClosedCaptioning} fontSize={24} />
            <Typography>{t('campaignDashboard.yourVideo.info.subtitles')}</Typography>
            <Tags
              fontWeight={theme.typography.fontWeightBold}
              useChips={false}
              tags={Object.keys(video.subtitle_tracks).map((tag) => ({
                label: tag.toUpperCase(),
              }))}
              limit={7}
            />
          </Box>
          {((video.quizzes && video.quizzes.length > 0 && video?.quiz_languages) ||
            (video?.quiz_questions_with_answers && video?.quiz_questions_with_answers?.length > 0)) && (
            <Box display="flex" gap={1} alignItems="center">
              <FontAwesomeIcon icon={faBlockQuestion} fontSize={24} />
              <Typography>{t('campaignDashboard.yourVideo.info.quizLanguages')}</Typography>
              <Tags
                fontWeight={theme.typography.fontWeightBold}
                useChips={false}
                tags={
                  (video?.quizzes
                    ? video?.quiz_languages?.map((tag) => ({
                        label: tag.toUpperCase(),
                      }))
                    : Object.keys(video?.quiz_questions_with_answers[0].question_translations).map((tag) => ({
                        label: tag.toUpperCase(),
                      }))) || []
                }
                limit={7}
              />
            </Box>
          )}
          <Box display="flex" gap={1} alignItems="center" marginTop={-1}>
            <FontAwesomeIcon icon={faShapes} fontSize={24} />
            <Typography>{t('campaignDashboard.yourVideo.info.topics')}</Typography>
            <Tags
              variant="outlined"
              tags={video.tags.map((tag) => ({
                label: tag,
                color: 'transparent',
                textColor: theme.palette.text.secondary,
              }))}
              limit={3}
            />
          </Box>
        </Box>
      </Box>

      <Box display="flex" gap={1} paddingTop={4}>
        <Box></Box>
        <FontAwesomeIcon icon={faCircleInfo} fontSize={24} />
        <Typography>
          <span style={{ fontWeight: theme.typography.fontWeightSemiBold }}>
            {t('campaignDashboard.yourVideo.summary')}
          </span>
          {video.description}
        </Typography>
      </Box>

      <FullModal
        open={isModalOpen}
        onClose={closeModal}
        title={t('campaignDashboard.yourVideo.modal.title')}
        buttonLabels={{ cancel: t('campaignDashboard.yourVideo.modal.close') }}>
        {video && <VideoPlayerV2 subtitleTracks={video.subtitle_tracks} videoTracks={video.video_tracks} />}
      </FullModal>
    </>
  )
}

const StyledPlayWrapper = styled(Box)(() => ({
  color: theme.palette.common.white,
  position: 'absolute',
  zIndex: 1,
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}))

const StyledTimeWrapper = styled(Box)(() => ({
  background: theme.palette.black + 70,
  padding: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1),
  color: theme.palette.common.white,
  position: 'absolute',
  right: theme.spacing(1),
  bottom: theme.spacing(1),
  fontWeight: theme.typography.fontWeightMedium,
}))

const StyledThumbnail = styled(Card)(() => ({
  minWidth: 388,
  width: 458,
  height: 266,
  position: 'relative',
  border: `1px solid ${theme.palette.grey[300]}`,
  borderRadius: theme.spacing(2),
  padding: 0,
  cursor: 'pointer',
  '&:hover': {
    transform: 'scale(1.01)',
  },
}))

export default VideoPreview
